"use client";

import type { ImpactArtifactsBlock } from "@mw/shared/payload-types";

import { clsx } from "clsx";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { type ReactNode, useCallback, useId, useRef } from "react";

import { Button } from "@/app/_ui/components/core/Button";
import { getImageProps, isEntity } from "@/app/_utilities/payload";
import { Dialog } from "@/app/_ui/components/core/DialogRedesign";
import { iconStrokeFix } from "@/app/_ui/central-icons/iconUtils";
import IconChevronLeftMedium from "@/app/_ui/central-icons/icons/IconChevronLeftMedium";
import IconChevronRightMedium from "@/app/_ui/central-icons/icons/IconChevronRightMedium";
import { sizes } from "@/app/_ui/lib/sizes";
import { ICON_ALIGN_OPTIONS } from "@/app/_ui/tokens/icon-types";

import { hasRenderableDialogContent } from "./ImpactArtifactsGallery.utils";
import { useImpactArtifactsDialog } from "./useImpactArtifactsDialog";
import { useImpactArtifactsMotion } from "./useImpactArtifactsMotion";
import { useImpactArtifactsScrollControls } from "./useImpactArtifactsScrollControls";

type Artifacts = ImpactArtifactsBlock["artifacts"];
type Artifact = Artifacts[number];

interface ImpactArtifactsGalleryProps {
  artifactDialogContents: Array<ReactNode | null>;
  artifacts: Artifacts;
}

interface ArtifactCardWrapperProps {
  children: ReactNode;
  hasDialogContent: boolean;
  onOpen: () => void;
}

const desktopArrangement = [
  "lg:-tw-rotate-6 lg:tw-translate-y-12",
  "lg:tw-rotate-0",
  "lg:tw-rotate-6 lg:tw-translate-y-14",
  "lg:tw-rotate-3 lg:tw-translate-y-4",
  "lg:-tw-rotate-3 lg:tw-translate-y-14",
];

const artifactsGalleryClassName = clsx(
  "-tw-mx-full-bleed tw-flex tw-items-start tw-gap-3 tw-overflow-x-auto tw-overflow-y-hidden tw-overscroll-x-contain",
  "tw-scroll-px-full-bleed tw-snap-x tw-snap-mandatory tw-scroll-smooth tw-scrollbar-none tw-px-full-bleed",
  "md:tw-gap-4",
  "lg:tw-mx-0 lg:tw-justify-center lg:tw-gap-0 lg:tw-overflow-visible lg:tw-px-0 lg:tw-scroll-px-0 lg:tw-snap-none",
);

const artifactCardShellClassName = clsx(
  "tw-block tw-h-full tw-w-full tw-rounded-3xl tw-border-0 tw-bg-transparent tw-p-0",
  "tw-text-left tw-outline-none",
);

const artifactCardClassName = clsx(
  "tw-group tw-flex tw-w-full tw-flex-col tw-gap-4 tw-rounded-3xl tw-border tw-border-black/10",
  "tw-bg-white tw-p-2.5 tw-pb-5 tw-text-left tw-shadow-sm tw-outline-none",
);

const artifactImageSizes = sizes("calc(100vw - 2.5rem)", {
  md: "calc(50vw - 2.5rem)",
  lg: "min(calc(22vw - 1.25rem), 16.6875rem)",
});

function getArtifactCardImageProps(image: Artifact["image"]) {
  if (!isEntity(image) || !image.width || !image.height) {
    return null;
  }

  return getImageProps(image);
}

export const ImpactArtifactsGallery = ({ artifactDialogContents, artifacts }: ImpactArtifactsGalleryProps) => {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const galleryId = useId();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { applyPointerPush, resetCardMotion, resetPointerTracking } = useImpactArtifactsMotion(scrollerRef);
  const { canScrollNext, canScrollPrev, scrollByStep } = useImpactArtifactsScrollControls(
    scrollerRef,
    artifacts.length,
  );
  const resetGalleryInteraction = useCallback(() => {
    resetPointerTracking();
    resetCardMotion();
  }, [resetCardMotion, resetPointerTracking]);
  const {
    isArtifactDialogOpen,
    openArtifactDialog,
    selectedArtifact,
    selectedArtifactDialogContent,
    setIsArtifactDialogOpen,
  } = useImpactArtifactsDialog({
    artifactDialogContents,
    artifacts,
    onBeforeOpen: resetGalleryInteraction,
  });

  return (
    <>
      <div
        className={artifactsGalleryClassName}
        id={galleryId}
        onPointerEnter={resetPointerTracking}
        onPointerLeave={resetPointerTracking}
        onPointerMove={applyPointerPush}
        ref={scrollerRef}
      >
        {artifacts.map((artifact, index) => {
          const cardImageProps = getArtifactCardImageProps(artifact.image);
          const artifactDialogContent = artifactDialogContents[index] ?? null;
          const hasDialogContent = hasRenderableDialogContent(artifactDialogContent);
          const titleId = `${galleryId}-artifact-title-${index}`;
          const cardContent = (
            <>
              <div className='tw-relative tw-w-full tw-overflow-hidden tw-rounded-2xl tw-bg-[#F5F3F1]'>
                {cardImageProps && (
                  <Image {...cardImageProps} className='tw-h-auto tw-w-full' sizes={artifactImageSizes} />
                )}
              </div>
              <p className='tw-type-sm tw-px-5 tw-text-center tw-text-gray-700 tw-opacity-100 lg:tw-opacity-60 lg:tw-transition-opacity lg:group-hover:tw-opacity-100'>
                <span id={titleId}>{artifact.title}</span>
              </p>
            </>
          );

          return (
            <article
              aria-labelledby={titleId}
              aria-roledescription='slide'
              className={clsx(
                "tw-basis-[calc(100%-1.25rem)] tw-shrink-0 tw-snap-start",
                "md:tw-basis-[calc((100%-2.5rem)/2)]",
                "lg:tw-basis-[22%] lg:tw-shrink-0",
                index > 0 && "lg:-tw-ml-8",
                desktopArrangement[index % desktopArrangement.length],
              )}
              data-snap-item
              key={artifact.id ?? `${artifact.title}-${index}`}
              role='group'
            >
              <ArtifactCardWrapper hasDialogContent={hasDialogContent} onOpen={() => openArtifactDialog(index)}>
                {cardContent}
              </ArtifactCardWrapper>
            </article>
          );
        })}
      </div>

      {artifacts.length > 1 && (
        <div className='tw-mt-8 tw-flex tw-justify-end tw-gap-4 lg:tw-hidden'>
          <Button
            aria-controls={galleryId}
            aria-label={tGlobals("previous")}
            disabled={!canScrollPrev}
            icon={<IconChevronLeftMedium className={clsx(iconStrokeFix, "tw-size-6")} />}
            iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            onClick={() => scrollByStep(-1)}
            size='pill-lg'
            type='button'
            variant='elevated'
          />
          <Button
            aria-controls={galleryId}
            aria-label={tGlobals("next")}
            disabled={!canScrollNext}
            icon={<IconChevronRightMedium className={clsx(iconStrokeFix, "tw-size-6")} />}
            iconPosition={ICON_ALIGN_OPTIONS.AFTER}
            onClick={() => scrollByStep(1)}
            size='pill-lg'
            type='button'
            variant='elevated'
          />
        </div>
      )}

      {selectedArtifact && (
        <Dialog
          isDismissable
          isOpen={isArtifactDialogOpen}
          onOpenChange={setIsArtifactDialogOpen}
          title={selectedArtifact.title}
          width={720}
        >
          {selectedArtifactDialogContent}
        </Dialog>
      )}
    </>
  );
};

function ArtifactCardWrapper({ children, hasDialogContent, onOpen }: ArtifactCardWrapperProps) {
  if (!hasDialogContent) {
    return (
      <div className={artifactCardClassName} data-impact-artifact-card>
        {children}
      </div>
    );
  }

  return (
    <button
      className={clsx(artifactCardShellClassName, "tw-group tw-cursor-pointer tw-appearance-none")}
      onClick={onOpen}
      type='button'
    >
      <div className={clsx(artifactCardClassName, "group-focus-visible:tw-outline-focus")} data-impact-artifact-card>
        {children}
      </div>
    </button>
  );
}
